import { Identified } from "@/types/sdk-types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { GridSortModel } from "@mui/x-data-grid";
import {
  DGT_Pagination,
  useBaseContent,
  getPageIndexWhileSearch,
  getConfigObject,
} from "../../../utils/utils";
import {
  GetPatientOrdersOrderTypeEnum,
  PatientOrderControllerApi,
  PatientOrder,
} from "../../../services";
import { useEncounterCreationGate } from "../../../hooks/use-encounter-creation-gate";

interface Props {
  patientUuid?: string;
  pagination: DGT_Pagination;
  gridSorting?: GridSortModel;
  appointmentUuid?: string;
  active?: boolean;
  labUuid?: string;
  orderType?: GetPatientOrdersOrderTypeEnum;
  archive?: boolean;
  orderStatus?: string;
  xTENANTID?: string;
  enabled?: boolean;
}

/**
 *
 * @author Rajkumar Rathod
 */
export const useGetOrders = ({
  patientUuid,
  pagination,
  gridSorting,
  labUuid,
  appointmentUuid,
  active,
  orderType,
  archive,
  orderStatus,
  xTENANTID,
  enabled = true,
}: Props): useBaseContent<Identified<PatientOrder>> => {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: "",
    prevPage,
  });

  const sortData = gridSorting?.[0];
  const skip = useEncounterCreationGate();
  return useQuery({
    queryKey: [
      "patient-orders-list",
      patientUuid,
      pageIndex,
      pagination.pageSize,
      sortData?.field, // sortBy
      labUuid,
      sortData?.sort ?? undefined,
      appointmentUuid,
      active,
      orderType,
      archive,
      orderStatus,
      xTENANTID,
    ],
    enabled: !skip && enabled,
    refetchOnWindowFocus: false,

    placeholderData: keepPreviousData,
    queryFn: async () =>
      await new PatientOrderControllerApi(getConfigObject())
        .getPatientOrders(
          pagination.page,
          pagination.pageSize,
          sortData?.field, // sortBy
          sortData?.sort ?? undefined, // sortDirection
          patientUuid,
          labUuid,
          appointmentUuid,
          active,
          orderType,
          archive,
          orderStatus,
          xTENANTID // xTENANTID
        )
        .then((res) => res?.data),
  });
};
